import { useCallback, useEffect, useRef, useState } from "react";
import { billsApi } from "@/lib/billsNative";
import { useSession } from "@/contexts/SessionContext";

// Availability of the Bills-native datastore.
//
// Bills and Expenses are PRODUCT AREAS: they stay in the navigation whatever this returns. A failure here
// disables actions and explains itself; it never hides the product.
//
// Two defects this replaces: the old hook cached a single module-level promise (one false answer stuck for
// the whole browser session) and it probed before an outlet was selected, where the readiness route answers
// 412 outlet_not_selected for any operator who has more than one outlet.
const RETRY_MS = 20000;
const MAX_AUTO = 10;

export function useBillsAvailability() {
  const { currentOutletId: outletId } = useSession();
  const [state, setState] = useState(outletId ? "checking" : "waiting-outlet");
  const [detail, setDetail] = useState(null);
  const attempts = useRef(0);
  const timer = useRef(null);
  const live = useRef(true);

  const probe = useCallback(async () => {
    if (!outletId) { setState("waiting-outlet"); return; }
    setState((s) => (s === "ready" ? "ready" : "checking"));
    const r = await billsApi.readiness();
    if (!live.current) return;
    if (r.ok && r.data?.ready) {
      attempts.current = 0;
      setDetail(null);
      setState("ready");
      return;
    }
    // 412 means no outlet is selected yet — not an outage; wait for the selection instead of burning retries.
    if (r.status === 412) { setState("waiting-outlet"); return; }
    setDetail(r.data || null);
    setState("unavailable");
  }, [outletId]);

  const retry = useCallback(() => { attempts.current = 0; probe(); }, [probe]);

  useEffect(() => {
    live.current = true;
    attempts.current = 0;
    probe();
    return () => { live.current = false; clearTimeout(timer.current); };
  }, [probe]);

  // Bounded recovery: re-check a few times after a transient outage so the operator never has to reload.
  useEffect(() => {
    clearTimeout(timer.current);
    if (state !== "unavailable" || attempts.current >= MAX_AUTO) return undefined;
    timer.current = setTimeout(() => { attempts.current += 1; probe(); }, RETRY_MS);
    return () => clearTimeout(timer.current);
  }, [state, probe]);

  return { state, ready: state === "ready", detail, retry };
}
